import { Dialog, paperClasses } from '@mui/material';
import { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  FilterDate,
  FilterSet,
  FilterText,
} from '../../../../../shared/api/hscan/case';
import bodyPartIcon from '../../../assets/icons/icon_bodypart.svg';
import calendarIcon from '../../../assets/icons/icon_calendar.svg';
import cameraIcon from '../../../assets/icons/icon_camera.svg';
import searchIcon from '../../../assets/icons/icon_search.svg';
import { useConvertToModality } from '../../../hooks/modalityHook';
import { BlueRoundedButton } from '../BlueRoundedButton';
import { ColoredIcon } from '../Icon';
import { CloseButtonContainer, ModalCloseButton } from '../ModalCloseButton';
import { PeriodSlot } from '../Slot/DateFieldSlot';
import { TextFieldSlot } from '../Slot/TextFieldSlot';

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  height: inherit;
  padding: 26px 25px 18px 25px;
  background-color: #ffffff;
  z-index: 1;
`;

const ModalHeading = styled.span`
  align-self: baseline;
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
  color: ${p => 'var(--HScan_Light_Blue, #2e5be0)' ?? p.theme.colors.black2};
`;

const SlotWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 17px;
  gap: 6px;
  max-width: 400px;
`;

const StyledDialog = styled(Dialog)`
  & .${paperClasses.root} {
    width: 100%;
    max-width: 450px;
    margin-right: 0;
    margin-left: 0;
  }
`;

export interface CaseFilter {
  general?: FilterText;
  studyDate?: FilterDate;
  modalities?: FilterSet;
  bodypart?: FilterText;
}
export interface SearchModalProps {
  open: boolean;
  onClose: (filter: CaseFilter) => void;
  onSearch?: (filter: CaseFilter) => void;
  defaultGeneralValue?: string;
}
export const SearchModal: React.FC<SearchModalProps> = (
  props: SearchModalProps,
) => {
  const { open, onClose, onSearch, defaultGeneralValue } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.myImageList.searchDetails',
  });

  const [general, setGeneral] = useState('');
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [modality, setModality] = useState('');
  const [bodypart, setBodypart] = useState('');

  useEffect(() => {
    setGeneral(defaultGeneralValue ?? '');
  }, [defaultGeneralValue]);

  const convertToModality = useConvertToModality();
  const filter: CaseFilter = useMemo(
    () => ({
      general: general?.length
        ? { filter: general, type: 'containsIgnoreCase' }
        : undefined,
      studyDate: startDate
        ? endDate
          ? {
              date: startDate.format('YYYYMMDD'),
              date2: endDate.format('YYYYMMDD'),
              type: 'between',
            }
          : {
              date: startDate.format('YYYYMMDD'),
              type: 'after',
            }
        : endDate
        ? {
            date: endDate.format('YYYYMMDD'),
            type: 'before',
          }
        : undefined,
      modalities: modality?.length
        ? { values: convertToModality(modality) }
        : undefined,
      bodypart: bodypart?.length
        ? { filter: bodypart, type: 'containsIgnoreCase' }
        : undefined,
    }),
    [bodypart, convertToModality, endDate, general, modality, startDate],
  );
  const onChangeFilter = () => {
    onSearch?.(filter);
  };

  const dateInvalid = !!startDate && !!endDate && startDate >= endDate;

  return (
    <StyledDialog onClose={() => onClose(filter)} open={open}>
      <Container>
        <CloseButtonContainer>
          <ModalCloseButton onClick={() => onClose(filter)} />
        </CloseButtonContainer>
        <ModalHeading>{t('heading')}</ModalHeading>
        <SlotWrapper>
          <TextFieldSlot
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={searchIcon}
                imageWidth="18px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            label={`${t('slots.general.title')}:`}
            placeholder={`${t('slots.general.placeholder')}`}
            value={general}
            onChange={value => setGeneral(value)}
          />
          <PeriodSlot
            label={`${t('slots.imageDate.title')}:`}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={calendarIcon}
                imageWidth="18px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            startDate={startDate}
            setStartDate={value => setStartDate(value)}
            endDate={endDate}
            setEndDate={value => setEndDate(value)}
            invalid={dateInvalid}
          />
          <TextFieldSlot
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={cameraIcon}
                imageWidth="24px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            label={`${t('slots.modality.title')}:`}
            placeholder={`${t('slots.modality.placeholder')}`}
            value={modality}
            onChange={value => setModality(value)}
          />
          <TextFieldSlot
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={bodyPartIcon}
                imageWidth="18px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            label={`${t('slots.bodypart.title')}:`}
            placeholder={`${t('slots.bodypart.placeholder')}`}
            value={bodypart}
            onChange={value => setBodypart(value)}
          />
        </SlotWrapper>
        <BlueRoundedButton
          maxWidth="130px"
          marginTop="16px"
          disabled={dateInvalid}
          onClick={() => {
            onChangeFilter();
            onClose(filter);
          }}>
          {t('button')}
        </BlueRoundedButton>
      </Container>
    </StyledDialog>
  );
};
